export type PaymentMethod =
    | 'CARD_STRIPE'
    | 'CASH'
    | 'PIX'
    | 'ETRANSFER'
    | 'BANK_TRANSFER'
    | 'GIFT_CARD'
    | 'CONVENIO'
    | 'OTHER';

export type TransactionStatus =
    | 'PENDING'
    | 'SUCCEEDED'
    | 'FAILED'
    | 'REFUNDED'
    | 'PARTIALLY_REFUNDED'
    | 'CANCELED';

export interface Transaction {
    id: string;
    businessId: string;
    appointmentId?: string | null;
    /** Snapshot of the appointment total at the time of this transaction. */
    appointmentTotal?: number | null;
    /** Event this payment belongs to (workshop participant), when set. */
    eventId?: string | null;
    eventParticipantId?: string | null;
    /** Gift-card code informed at charge time (method = GIFT_CARD). */
    giftCardCode?: string | null;
    clientId?: string | null;
    customerName?: string | null;
    amount: number;
    currency: string;
    discountAmount: number;
    platformFee: number;
    netAmount: number;
    method: PaymentMethod;
    status: TransactionStatus;
    stripePaymentIntentId?: string | null;
    stripeChargeId?: string | null;
    description?: string | null;
    notes?: string | null;
    refundedAmount: number;
    refundedAt?: string | null;
    createdBy?: string | null;
    createdOn: string;
    updatedOn: string;
    /** Soft-deleted transactions stay in the list (strikethrough) but are skipped by stats. */
    deleted?: boolean | null;
    deletedOn?: string | null;
    deletedBy?: string | null;
}

export interface TransactionPage {
    items: Transaction[];
    total: number;
    page: number;
    perPage: number;
}

export interface MethodBreakdown {
    method: PaymentMethod;
    total: number;
    count: number;
}

export interface TransactionStats {
    grossTotal: number;
    netTotal: number;
    refundedTotal: number;
    count: number;
    averageTicket: number;
    byMethod: MethodBreakdown[];
}

export interface TransactionFilters {
    start?: string | null;
    end?: string | null;
    status?: TransactionStatus | null;
    method?: PaymentMethod | null;
    clientId?: string | null;
    /** Exact transaction id — matches that one payment, ignoring the date range. */
    id?: string | null;
    /** Exact appointment id — returns every payment of that appointment, ignoring the date range. */
    appointmentId?: string | null;
    search?: string | null;
    page?: number;
    perPage?: number;
}

export interface ManualPaymentInput {
    businessId: string;
    appointmentId?: string | null;
    /** Event this payment belongs to (with eventParticipantId, for a workshop participant). */
    eventId?: string | null;
    /** The specific event participant paying. */
    eventParticipantId?: string | null;
    /** Required when method = GIFT_CARD — the card's printed code. */
    giftCardCode?: string | null;
    /** Snapshot of the appointment's total amount due — drives PARTIAL_PAID detection. */
    appointmentTotal?: number | null;
    clientId?: string | null;
    customerName?: string | null;
    amount: number;
    currency?: string | null;
    discountAmount?: number | null;
    method: PaymentMethod;
    description?: string | null;
    notes?: string | null;
}

/**
 * Fallback English labels — prefer `t('payments.method.' + m)` in components
 * so the label follows the UI language. Kept for non-React contexts (exports).
 */
export const PAYMENT_METHOD_LABEL: Record<PaymentMethod, string> = {
    CARD_STRIPE: 'Card (Stripe)',
    CASH: 'Cash',
    PIX: 'PIX',
    ETRANSFER: 'e-Transfer',
    BANK_TRANSFER: 'Bank transfer',
    GIFT_CARD: 'Gift card',
    CONVENIO: 'Convênio',
    OTHER: 'Other',
};

export const TRANSACTION_STATUS_LABEL: Record<TransactionStatus, string> = {
    PENDING: 'Pending',
    SUCCEEDED: 'Succeeded',
    FAILED: 'Failed',
    REFUNDED: 'Refunded',
    PARTIALLY_REFUNDED: 'Partial refund',
    CANCELED: 'Canceled',
};

/** Default order — every selectable method ("Other" retired 2026-07-12; the
 *  code stays in PaymentMethod/labels only so legacy transactions render). */
export const ALL_PAYMENT_METHODS: PaymentMethod[] = [
    'CARD_STRIPE', 'CASH', 'PIX', 'ETRANSFER', 'BANK_TRANSFER', 'GIFT_CARD',
];

/**
 * Methods available for a business country: PIX exists only in Brazil,
 * e-Transfer (Interac) only in Canada. Unknown/absent country shows both.
 */
export const methodsForCountry = (
    country: string | null | undefined
): PaymentMethod[] => {
    const c = (country ?? '').trim().toLowerCase();
    return ALL_PAYMENT_METHODS.filter((m) => {
        if (m === 'PIX') return c === '' || c === 'brazil' || c === 'brasil' || c === 'br';
        if (m === 'ETRANSFER') return c === '' || c === 'canada' || c === 'canadá' || c === 'ca';
        return true;
    });
};

/**
 * Parse `Business.acceptedPaymentMethods` (CSV) into an ordered list of
 * methods. Empty/null/whitespace = accept all (for the business country).
 * Country-invalid codes (e.g. PIX saved before the business moved off Brazil)
 * are dropped so they never show up as charge options.
 */
export const parseAcceptedMethods = (
    raw: string | null | undefined,
    country?: string | null
): PaymentMethod[] => {
    const allowed = methodsForCountry(country);
    if (!raw || !raw.trim()) return allowed;
    const set = new Set<PaymentMethod>();
    raw.split(',').forEach((part) => {
        const code = part.trim() as PaymentMethod;
        if (allowed.includes(code)) set.add(code);
    });
    const list = allowed.filter((m) => set.has(m));
    return list.length > 0 ? list : allowed;
};
